import {
  GoogleMapsUsageEvent,
  GoogleMapsUsageEventInput
} from './GoogleMapsUsageEvent';
import { RestfulUILogWebService, UILogWebService } from 'ws/UILogWebService';
import config from 'config';

// In-memory buffer + best-effort flush. Records are dropped when
// channelGroupId is unknown (via useGoogleMapsUsage) so aggregation stays
// clean. Long map sessions guarded by MAX_BUFFER_SIZE with eager flush.
// beforeunload uses `navigator.sendBeacon` so the final flush survives tab
// close. No user identity is tracked or logged — the BE writes the event
// verbatim, so the usage log is PII-free (keyed only by channelGroupId).

const MAX_BUFFER_SIZE = 500;
const ENDPOINT_PATH = '/v2/ui-logs/google-maps';

export interface GoogleMapsUsageTracker {
  record(event: GoogleMapsUsageEventInput): void;
  flush(): Promise<void>;
  flushOnBeforeUnload(): void;
}

export class DefaultGoogleMapsUsageTracker implements GoogleMapsUsageTracker {
  private buffer: GoogleMapsUsageEvent[] = [];
  private inflight?: Promise<void>;

  constructor(
    private webService: UILogWebService = new RestfulUILogWebService()
  ) {}

  record(event: GoogleMapsUsageEventInput): void {
    this.buffer.push({
      ...event,
      timestamp: new Date().toISOString()
    } as GoogleMapsUsageEvent);
    if (this.buffer.length >= MAX_BUFFER_SIZE) {
      // Runaway session guard: don't wait for map close.
      void this.flush();
    }
  }

  async flush(): Promise<void> {
    if (this.buffer.length === 0) return;
    // Serialize concurrent flushes so a map-close + unmount pair doesn't
    // double-send.
    if (this.inflight) {
      await this.inflight;
      return;
    }
    const events = this.buffer;
    this.buffer = [];
    this.inflight = (async () => {
      try {
        const { dropped } = await this.webService.postGoogleMapsUsage(events);
        if (dropped > 0) {
          console.warn(
            `[GoogleMapsUsageTracker] BE rate limit dropped ${dropped} events`
          );
        }
      } catch (error) {
        // Telemetry is best-effort; a failed flush drops the buffer's contents
        // rather than growing the queue indefinitely. Surface for dev.
        console.debug(
          '[GoogleMapsUsageTracker] flush failed',
          error instanceof Error ? error.message : error
        );
      } finally {
        this.inflight = undefined;
      }
    })();
    await this.inflight;
  }

  flushOnBeforeUnload(): void {
    if (this.buffer.length === 0) return;
    // fetch() from a beforeunload handler is often cancelled by the browser;
    // sendBeacon is the reliable transport. The payload matches the axios POST
    // body so the same BE controller handles both.
    const payload = JSON.stringify({ events: this.buffer });
    const blob = new Blob([payload], { type: 'application/json' });
    // Absolute URL required — sendBeacon doesn't respect axios baseURL.
    const url = `${config.baseURL}${ENDPOINT_PATH}`;
    const sent = navigator.sendBeacon(url, blob);
    if (sent) {
      this.buffer = [];
    }
  }
}

export const googleMapsUsageTracker: GoogleMapsUsageTracker =
  new DefaultGoogleMapsUsageTracker();
